/**
 * The wait clock both SkillFit waiting screens read.
 *
 * Each party held before an interview is bounded by its own wait, measured from the start of that
 * party's current sitting. ⛔ Neither screen shows the other party's expiry: each is released on its
 * own clock, and once a party can restart their wait by reopening their link the two instants are
 * not even measured from the same event. What a waiting screen still has to reckon with is the
 * window that bounds how long the pair may sit in the room before starting — see below.
 *
 * Lives here rather than in either screen so there is ONE formatter and ONE comparison: the two
 * screens answer the same question for two different people — ⛔ no longer with the same instant —
 * and a second copy of the reckoning is how they start disagreeing about how to answer it.
 */

/**
 * The release instant as a local wall-clock time, or `null` when there is nothing to show.
 *
 * Returns null — never a fallback string — for an absent or unparseable value, so the sentence
 * carrying it is dropped whole rather than promising a release at "Invalid Date". The expiry is
 * legitimately null until the first tick records the party's presence, which is what the wait is
 * measured from.
 */
export const releaseTimeLabel = isoString => {
  if (!isoString) return null;
  const at = new Date(isoString);
  if (Number.isNaN(at.getTime())) return null;
  return at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

/**
 * The earliest of any number of expiries, as the ISO string that was passed in, or `null` when none
 * is usable.
 *
 * ⭐ Any number, because a waiting party is bounded by more than their own wait: the window that
 * bounds how long both parties may sit in the room before starting begins when the FIRST of them
 * enters, so it can run out while the second is still on the waiting screen — and it is the one
 * that ends the attempt. A screen naming a later deadline has not made a smaller promise than one
 * naming none; it has made a wrong one.
 *
 * Unparseable values are discarded rather than compared: `NaN < x` is false in both directions, so
 * an unguarded comparison would silently elect the bad value and render a deadline of "Invalid
 * Date" — or, worse, hide a real one.
 */
export const earlierIso = (...isos) => {
  const usable = isos
    .filter(Boolean)
    .map(iso => ({ iso, ms: new Date(iso).getTime() }))
    .filter(({ ms }) => !Number.isNaN(ms));

  if (usable.length === 0) return null;
  return usable.reduce((earliest, next) => (next.ms < earliest.ms ? next : earliest)).iso;
};
